import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  Slider,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandFacebook,
  IconDownload,
  IconPlugConnected,
  IconArrowLeft,
  IconRocket,
  IconX,
  IconCurrencyDollar,
  IconCalendarEvent,
  IconUsers,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import Chart from "react-apexcharts";
import Button from "../components/Button";
import { fetchGet, fetchPost } from "../util/helpers";

const BOOST_AUDIENCES = [
  "Lookalike Audience",
  "Custom Audience",
  "Interest-based",
  "Retargeting",
  "Broad Audience",
];

const platformIcon = {
  facebook: <IconBrandFacebook size={16} />,
  instagram: <IconBrandInstagram size={16} />,
  tiktok: <IconBrandTiktok size={16} />,
  youtube: <IconBrandYoutube size={16} />,
  linkedin: <IconBrandLinkedin size={16} />,
  twitter: <IconBrandTwitter size={16} />,
};

const platformColor = {
  facebook: "#1877F2",
  instagram: "#C13584",
  tiktok: "#000",
  youtube: "#FF0000",
  linkedin: "#0A66C2",
  twitter: "#1DA1F2",
};

const STATUS_COLORS = {
  draft: "default",
  queued: "info",
  pending_approval: "warning",
  published: "success",
  boosted: "secondary",
};

const shimmerKeyframes = {
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "-400px 0" },
    "100%": { backgroundPosition: "400px 0" },
  },
};

const STATUS_GLOW = {
  draft: "rgba(158,158,158,0.35)",
  queued: "rgba(33,150,243,0.35)",
  pending_approval: "rgba(255,152,0,0.35)",
  published: "rgba(76,175,80,0.35)",
  boosted: "rgba(156,39,176,0.35)",
};

const PASTEL_TAG_COLORS = [
  { bg: "#F3E8FF", color: "#7C3AED" },
  { bg: "#E0F2FE", color: "#0284C7" },
  { bg: "#FCE7F3", color: "#DB2777" },
  { bg: "#ECFDF5", color: "#059669" },
  { bg: "#FEF3C7", color: "#D97706" },
  { bg: "#EDE9FE", color: "#6D28D9" },
  { bg: "#FFE4E6", color: "#E11D48" },
];

function PostTab({ post }) {
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 0,
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(159,37,246,0.08)",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #9F25F6, #6366F1, #3B82F6)",
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 2.5,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #1a1a2e, #4a4a6a)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Post Details
              </Typography>
              <Stack spacing={2.5}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1.2,
                      color: "text.secondary",
                      mb: 0.75,
                    }}
                  >
                    Platforms
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {post.platforms?.map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        icon={platformIcon[p]}
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 600,
                          fontSize: 12,
                          border: `1.5px solid ${platformColor[p]}`,
                          color: platformColor[p],
                          bgcolor: `${platformColor[p]}10`,
                          "& .MuiChip-icon": { color: platformColor[p] },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1.2,
                      color: "text.secondary",
                      mb: 0.75,
                    }}
                  >
                    Status
                  </Typography>
                  <Chip
                    label={post.status?.replace("_", " ")}
                    size="small"
                    color={STATUS_COLORS[post.status] || "default"}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 600,
                      borderRadius: "8px",
                      boxShadow: `0 0 12px ${STATUS_GLOW[post.status] || "rgba(0,0,0,0.1)"}`,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1.2,
                      color: "text.secondary",
                      mb: 0.75,
                    }}
                  >
                    Caption
                  </Typography>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      lineHeight: 1.7,
                      fontSize: 14,
                      color: "#334155",
                      bgcolor: "#F8FAFC",
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    {post.caption}
                  </Box>
                </Box>
                {post.tags?.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        color: "text.secondary",
                        mb: 0.75,
                      }}
                    >
                      Tags
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {post.tags.map((t, idx) => {
                        const pastel = PASTEL_TAG_COLORS[idx % PASTEL_TAG_COLORS.length];
                        return (
                          <Chip
                            key={t}
                            label={t}
                            size="small"
                            sx={{
                              bgcolor: pastel.bg,
                              color: pastel.color,
                              fontWeight: 600,
                              fontSize: 11,
                              borderRadius: "8px",
                              border: "none",
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {post.targetCountry && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        color: "text.secondary",
                        mb: 0.75,
                      }}
                    >
                      Target Country
                    </Typography>
                    <Typography variant="bodysm" sx={{ fontWeight: 500 }}>
                      {post.targetCountry}
                    </Typography>
                  </Box>
                )}
                {post.shortenedLink && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        color: "text.secondary",
                        mb: 0.75,
                      }}
                    >
                      Shortened Link
                    </Typography>
                    <Typography
                      variant="bodysm"
                      sx={{
                        color: "#7C3AED",
                        fontWeight: 500,
                        textDecoration: "underline",
                        textDecorationColor: "rgba(124,58,237,0.3)",
                      }}
                    >
                      {post.shortenedLink}
                    </Typography>
                  </Box>
                )}
                {post.scheduledAt && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        color: "text.secondary",
                        mb: 0.75,
                      }}
                    >
                      Scheduled
                    </Typography>
                    <Typography variant="bodysm" sx={{ fontWeight: 500 }}>
                      {dayjs(post.scheduledAt).format("MMM D, YYYY h:mm A")}
                    </Typography>
                  </Box>
                )}
                {post.boostSettings && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        color: "text.secondary",
                        mb: 0.75,
                      }}
                    >
                      Boost campaign
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(245,158,11,0.06)",
                        border: "1px solid rgba(245,158,11,0.2)",
                      }}
                    >
                      <Typography variant="bodysm" sx={{ fontWeight: 600, color: "#B45309", mb: 0.5 }}>
                        ${post.boostSettings.budget}/day · {post.boostSettings.duration} days · est. ${(post.boostSettings.budget || 0) * (post.boostSettings.duration || 0)} total
                      </Typography>
                      <Typography variant="bodysm" sx={{ color: "#78350F" }}>
                        Audience: {post.boostSettings.audience || "—"}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          {post.media?.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 0,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(59,130,246,0.08)",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: "linear-gradient(90deg, #3B82F6, #06B6D4, #10B981)",
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2.5,
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #1a1a2e, #4a4a6a)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Media
                </Typography>
                <Grid container spacing={1.5}>
                  {post.media.map((m, i) => (
                    <Grid item xs={6} key={i}>
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          position: "relative",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                          transition: "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.03)",
                            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        {m.fileType?.startsWith("video") ? (
                          <Box
                            component="video"
                            src={m.url}
                            controls
                            sx={{ width: "100%", display: "block" }}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={m.url}
                            alt={m.fileName}
                            sx={{
                              width: "100%",
                              display: "block",
                              objectFit: "cover",
                              aspectRatio: "1",
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function useMockData(len) {
  const [data] = useState(() => {
    const likes = Array.from({ length: len }, () => Math.floor(Math.random() * 300 + 20));
    const comments = Array.from({ length: len }, () => Math.floor(Math.random() * 80 + 5));
    const shares = Array.from({ length: len }, () => Math.floor(Math.random() * 50 + 2));
    const engagement = likes.map((l, i) => l + comments[i] + shares[i]);
    return {
      engagement,
      reach: Array.from({ length: len }, () => Math.floor(Math.random() * 500 + 50)),
      impressions: Array.from({ length: len }, () => Math.floor(Math.random() * 500 + 50)),
      likes,
      comments,
      shares,
    };
  });
  return data;
}

const formatNum = (n) => n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "k" : n.toLocaleString();

function AnalyticsTab({ post, onBoostClick }) {
  const [dateRange, setDateRange] = useState("30");
  const days = parseInt(dateRange);
  const mockData = useMockData(days);

  const totalEngagement = mockData.engagement.reduce((a, b) => a + b, 0);
  const totalReach = mockData.reach.reduce((a, b) => a + b, 0);
  const totalImpressions = mockData.impressions.reduce((a, b) => a + b, 0);
  const totalLikes = mockData.likes.reduce((a, b) => a + b, 0);
  const totalComments = mockData.comments.reduce((a, b) => a + b, 0);
  const totalShares = mockData.shares.reduce((a, b) => a + b, 0);
  const ctr = totalImpressions > 0 ? ((totalEngagement / totalImpressions) * 100).toFixed(1) + "%" : "0%";

  const xLabels = Array.from({ length: days }, (_, i) => dayjs().subtract(days - i - 1, "day").format("MMM D"));
  const xCategories = days >= 365
    ? xLabels.filter((_, i) => i % 7 === 0)
    : xLabels;

  const downsample = (arr) => days >= 365
    ? arr.filter((_, i) => i % 7 === 0)
    : arr;

  const chartOptions = {
    chart: { type: "area", toolbar: { show: false }, fontFamily: "Inter" },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: xCategories,
      labels: { rotate: days >= 365 ? -45 : 0, style: { fontSize: days >= 365 ? "9px" : "11px" } },
    },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } },
    colors: ["#9F25F6", "#1DA1F2", "#FF6B6B"],
    tooltip: { theme: "light" },
    dataLabels: { enabled: false },
  };

  const chartSeries = [
    { name: "Engagement", data: downsample(mockData.engagement) },
    { name: "Reach", data: downsample(mockData.reach) },
    { name: "Impressions", data: downsample(mockData.impressions) },
  ];

  const breakdownChartOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "Inter", stacked: false },
    plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
    stroke: { show: true, width: 1, colors: ["transparent"] },
    xaxis: {
      categories: xCategories,
      labels: { rotate: days >= 365 ? -45 : 0, style: { fontSize: days >= 365 ? "9px" : "11px" } },
    },
    colors: ["#F43F5E", "#8B5CF6", "#06B6D4"],
    tooltip: { theme: "light" },
    dataLabels: { enabled: false },
    legend: { position: "top" },
  };

  const breakdownSeries = [
    { name: "Likes", data: downsample(mockData.likes) },
    { name: "Comments", data: downsample(mockData.comments) },
    { name: "Shares", data: downsample(mockData.shares) },
  ];

  const handleDownloadReport = () => {
    const csv = `Metric,Value\nTotal Engagement,${totalEngagement}\nTotal Reach,${totalReach}\nImpressions,${totalImpressions}\nLikes,${totalLikes}\nComments,${totalComments}\nShares,${totalShares}\nCTR,${ctr}\nPost Status,${post.status || ""}\nPlatforms,${post.platforms?.join("; ") || ""}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `post_report_${post.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const launchDate = post.publishedAt
    ? dayjs(post.publishedAt.seconds ? new Date(post.publishedAt.seconds * 1000) : post.publishedAt).format("MMM D, YYYY")
    : post.scheduledAt
    ? dayjs(post.scheduledAt).format("MMM D, YYYY")
    : "Not set";

  const statCards = [
    { label: "Total Engagement", value: formatNum(totalEngagement), gradient: "linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)", accent: "#9F25F6", iconBg: "rgba(159,37,246,0.12)" },
    { label: "Total Reach", value: formatNum(totalReach), gradient: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)", accent: "#1DA1F2", iconBg: "rgba(29,161,242,0.12)" },
    { label: "Impressions", value: formatNum(totalImpressions), gradient: "linear-gradient(135deg, #FFE4E6 0%, #FECDD3 100%)", accent: "#FF6B6B", iconBg: "rgba(255,107,107,0.12)" },
    { label: "Click-through Rate", value: ctr, gradient: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", accent: "#10B981", iconBg: "rgba(16,185,129,0.12)" },
    { label: "Likes", value: formatNum(totalLikes), gradient: "linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 100%)", accent: "#F43F5E", iconBg: "rgba(244,63,94,0.12)" },
    { label: "Comments", value: formatNum(totalComments), gradient: "linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)", accent: "#8B5CF6", iconBg: "rgba(139,92,246,0.12)" },
    { label: "Shares", value: formatNum(totalShares), gradient: "linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)", accent: "#06B6D4", iconBg: "rgba(6,182,212,0.12)" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          mb: 3,
          background: "linear-gradient(135deg, #FAFAFE 0%, #F0EEFF 100%)",
          border: "1px solid rgba(159,37,246,0.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="bodysm"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Report Launch Date:{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
              {launchDate}
            </Box>
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
            {post.channels?.map((ch) => (
              <Chip
                key={ch}
                label={ch}
                size="small"
                sx={{
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: 11,
                  bgcolor: "#F1F5F9",
                  border: "1px solid #E2E8F0",
                }}
              />
            ))}
            {post.platforms?.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                icon={platformIcon[p]}
                sx={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: 11,
                  border: `1.5px solid ${platformColor[p]}`,
                  color: platformColor[p],
                  bgcolor: `${platformColor[p]}10`,
                  "& .MuiChip-icon": { color: platformColor[p] },
                }}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "white",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#9F25F6",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#9F25F6",
                },
              },
            }}
          >
            <InputLabel>Date Range</InputLabel>
            <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)} label="Date Range">
              <MenuItem value="7">Past 7 days</MenuItem>
              <MenuItem value="30">Past 30 days</MenuItem>
              <MenuItem value="90">Past 90 days</MenuItem>
              <MenuItem value="365">Past 1 year</MenuItem>
            </Select>
          </FormControl>
          <Button
            style={{
              borderRadius: 10,
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(159,37,246,0.2)",
            }}
            gradient
            onClick={handleDownloadReport}
          >
            <IconDownload size={16} style={{ marginRight: 6 }} /> Download Report
          </Button>
          {onBoostClick && (
            <Button
              style={{
                borderRadius: 10,
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 600,
                background: "linear-gradient(135deg, #F59E0B, #EF4444)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={onBoostClick}
            >
              <IconRocket size={16} /> Boost Post
            </Button>
          )}
          <Button
            style={{
              borderRadius: 10,
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
              color: "#4338CA",
              border: "1px solid rgba(99,102,241,0.15)",
            }}
          >
            <IconPlugConnected size={16} style={{ marginRight: 6 }} /> Connect Google Analytics
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {statCards.map((stat, idx) => (
          <Grid item xs={6} md={idx < 4 ? 3 : 4} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                textAlign: "center",
                background: stat.gradient,
                border: `1px solid ${stat.accent}15`,
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 25px ${stat.accent}20`,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: `${stat.accent}08`,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  color: stat.accent,
                  opacity: 0.8,
                  mb: 0.5,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: stat.accent,
                  fontWeight: 800,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(159,37,246,0.08)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: "linear-gradient(90deg, #9F25F6, #6366F1, #3B82F6)",
          },
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: "linear-gradient(135deg, #1a1a2e, #4a4a6a)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Performance Overview
          </Typography>
          <Chart options={chartOptions} series={chartSeries} type="area" height={320} />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(244,63,94,0.08)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: "linear-gradient(90deg, #F43F5E, #8B5CF6, #06B6D4)",
          },
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: "linear-gradient(135deg, #1a1a2e, #4a4a6a)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Engagement Breakdown
          </Typography>
          <Chart options={breakdownChartOptions} series={breakdownSeries} type="bar" height={280} />
        </Box>
      </Paper>
    </Box>
  );
}

export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [boostDialogOpen, setBoostDialogOpen] = useState(false);
  const [boostBudget, setBoostBudget] = useState(50);
  const [boostDuration, setBoostDuration] = useState(7);
  const [boostAudience, setBoostAudience] = useState("Broad Audience");
  const [boostSubmitting, setBoostSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const reloadPost = () => {
    fetchGet(`/social/posts/${postId}`)
      .then((res) => { if (res?.result) setPost(res.result); })
      .catch((e) => console.error("Error loading post:", e));
  };

  useEffect(() => {
    fetchGet(`/social/posts/${postId}`)
      .then((res) => { if (res?.result) setPost(res.result); })
      .catch((e) => console.error("Error loading post:", e))
      .finally(() => setLoading(false));
  }, [postId]);

  const openBoostDialog = () => {
    const b = post?.boostSettings;
    setBoostBudget(typeof b?.budget === "number" ? b.budget : 50);
    setBoostDuration(typeof b?.duration === "number" ? b.duration : 7);
    setBoostAudience(b?.audience && BOOST_AUDIENCES.includes(b.audience) ? b.audience : "Broad Audience");
    setBoostDialogOpen(true);
  };

  const handleBoostConfirm = async () => {
    setBoostSubmitting(true);
    try {
      await fetchPost(`/social/posts/${postId}/boost`, {
        boostSettings: {
          budget: boostBudget,
          duration: boostDuration,
          audience: boostAudience,
          currency: "USD",
        },
      });
      reloadPost();
      setBoostDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Boost saved: $${boostBudget}/day for ${boostDuration} days (est. $${boostBudget * boostDuration} total)`,
        severity: "success",
      });
    } catch (e) {
      console.error("Boost error:", e);
      setSnackbar({ open: true, message: "Could not save boost. Try again.", severity: "error" });
    }
    setBoostSubmitting(false);
  };

  if (loading)
    return (
      <Box sx={{ p: 4, minHeight: "100vh" }}>
        <Box
          sx={{
            maxWidth: 900,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {[200, 80, 400, 120, 300].map((w, i) => (
            <Box
              key={i}
              sx={{
                height: i === 2 ? 200 : i === 0 ? 56 : 40,
                maxWidth: w,
                width: "100%",
                borderRadius: 2,
                background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                backgroundSize: "800px 100%",
                animation: "shimmer 1.5s infinite linear",
                "@keyframes shimmer": {
                  "0%": { backgroundPosition: "-400px 0" },
                  "100%": { backgroundPosition: "400px 0" },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    );

  if (!post)
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #F3E8FF, #E0E7FF)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: 28 }}>?</Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#64748B" }}>
          Post not found
        </Typography>
        <Button
          style={{ borderRadius: 10, padding: "8px 20px", fontSize: 13, fontWeight: 600 }}
          gradient
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Box>
    );

  const postTitle = post.caption
    ? post.caption.length > 60
      ? post.caption.substring(0, 60) + "..."
      : post.caption
    : "Untitled Post";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFE" }}>
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a1a2e 0%, #2d1b69 40%, #44318d 100%)",
          pt: 4,
          pb: 6,
          px: { xs: 2, md: 4 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(159,37,246,0.15) 0%, transparent 70%)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -80,
            left: -50,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1200, mx: "auto" }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: "rgba(255,255,255,0.7)",
              mb: 2,
              border: "1px solid rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.12)",
                color: "white",
              },
            }}
          >
            <IconArrowLeft size={20} />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 800,
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {postTitle}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Post Details & Analytics
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}>
              <Chip
                label={post.status?.replace("_", " ")}
                size="small"
                color={STATUS_COLORS[post.status] || "default"}
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 700,
                  fontSize: 12,
                  px: 1,
                  height: 28,
                  borderRadius: "10px",
                  boxShadow: `0 0 20px ${STATUS_GLOW[post.status] || "rgba(255,255,255,0.2)"}`,
                }}
              />
              <Tooltip title="Run paid media behind this post" arrow>
                <span>
                  <Button
                    style={{
                      borderRadius: 10,
                      padding: "0 18px",
                      height: 36,
                      fontSize: 12,
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #F59E0B, #EF4444)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                    onClick={openBoostDialog}
                  >
                    <IconRocket size={16} /> Boost Post
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 4 },
          mt: -3,
          position: "relative",
          zIndex: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: { xs: 1, md: 3 },
              pt: 1.5,
              bgcolor: "#FAFAFF",
              borderBottom: "1px solid rgba(0,0,0,0.05)",
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                background: "linear-gradient(90deg, #9F25F6, #6366F1)",
              },
              "& .MuiTabs-flexContainer": {
                gap: 0.5,
              },
            }}
          >
            {["The Post", "Analytics"].map((label, idx) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  textTransform: "none",
                  fontWeight: tab === idx ? 700 : 500,
                  fontSize: 14,
                  color: tab === idx ? "#7C3AED" : "#94A3B8",
                  borderRadius: "12px 12px 0 0",
                  minHeight: 44,
                  px: 2.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(159,37,246,0.04)",
                    color: "#7C3AED",
                  },
                  "&.Mui-selected": {
                    color: "#7C3AED",
                    bgcolor: "rgba(159,37,246,0.04)",
                  },
                }}
              />
            ))}
          </Tabs>
          {tab === 0 && <PostTab post={post} />}
          {tab === 1 && <AnalyticsTab post={post} onBoostClick={openBoostDialog} />}
        </Paper>
      </Box>

      <Box sx={{ height: 40 }} />

      <Dialog
        open={boostDialogOpen}
        onClose={() => !boostSubmitting && setBoostDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconRocket size={18} color="#fff" />
            </Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              Boost this post
            </Typography>
          </Box>
          <IconButton onClick={() => !boostSubmitting && setBoostDialogOpen(false)} sx={{ color: "#fff" }} size="small">
            <IconX size={18} />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mb: 3, lineHeight: 1.6 }}>
            Put paid media behind this post to reach a wider audience. Adjust budget, duration, and targeting below.
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconCurrencyDollar size={16} color="#F59E0B" />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Daily budget</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Slider
                value={boostBudget}
                onChange={(_, val) => setBoostBudget(val)}
                min={5}
                max={500}
                step={5}
                sx={{
                  flex: 1,
                  color: "#F59E0B",
                  "& .MuiSlider-thumb": { boxShadow: "0 2px 8px rgba(245,158,11,0.3)" },
                }}
              />
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  minWidth: 70,
                  textAlign: "center",
                  bgcolor: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#D97706" }}>${boostBudget}</Typography>
              </Paper>
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconCalendarEvent size={16} color="#F59E0B" />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Duration (days)</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {[3, 7, 14, 30].map((d) => (
                <Box
                  key={d}
                  onClick={() => !boostSubmitting && setBoostDuration(d)}
                  sx={{
                    flex: 1,
                    py: 1,
                    borderRadius: 2,
                    textAlign: "center",
                    cursor: boostSubmitting ? "default" : "pointer",
                    border: boostDuration === d ? "2px solid #F59E0B" : "2px solid #E5E7EB",
                    bgcolor: boostDuration === d ? "rgba(245,158,11,0.06)" : "#fff",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: boostSubmitting ? undefined : "#F59E0B" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: boostDuration === d ? 700 : 500,
                      color: boostDuration === d ? "#D97706" : "#6B7280",
                    }}
                  >
                    {d}d
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 11, color: "#9CA3AF", mt: 0.75, textAlign: "right" }}>
              Est. total spend: ${boostBudget * boostDuration}
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconUsers size={16} color="#F59E0B" />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Target audience</Typography>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel id="boost-audience-label">Audience</InputLabel>
              <Select
                labelId="boost-audience-label"
                label="Audience"
                value={boostAudience}
                onChange={(e) => setBoostAudience(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {BOOST_AUDIENCES.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            style={{
              borderRadius: 10,
              padding: "8px 18px",
              fontSize: 12,
              fontWeight: 600,
              background: "#F3F4F6",
              color: "#374151",
            }}
            onClick={() => setBoostDialogOpen(false)}
            disabled={boostSubmitting}
          >
            Cancel
          </Button>
          <Button
            style={{
              borderRadius: 10,
              padding: "8px 22px",
              fontSize: 12,
              fontWeight: 700,
              background: "linear-gradient(135deg, #F59E0B, #EF4444)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={handleBoostConfirm}
            disabled={boostSubmitting}
          >
            <IconRocket size={14} /> {boostSubmitting ? "Saving…" : `Confirm boost ($${boostBudget * boostDuration})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3, fontWeight: 600, fontSize: 13 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
